import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  AppWindow,
  ChevronsLeft,
  ChevronsRight,
  DatabaseBackup,
  GitBranch,
  History,
  Key,
  KeyRound,
  LogOut,
  Moon,
  Search,
  SquareSlash,
  Sun,
  Tag,
  Users,
  Variable,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { FlowQuickSwitcher } from './FlowQuickSwitcher';
import { useTheme } from '../ThemeContext';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { cn } from '../lib/utils';

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
}

const COLLAPSED_KEY = 'sidebar-collapsed';

/** The builder canvas fills the whole content area; every other page gets padding. */
const FULL_BLEED = /^\/flows\/[^/]+$/;

export function Layout({ children }: { children: React.ReactNode }) {
  const { logout, role } = useAuth();
  const { pathname } = useLocation();
  const { isDark, toggleDark } = useTheme();

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // Storage unavailable — the sidebar just forgets its state.
    }
  }, [collapsed]);

  const topItems: NavItem[] = [
    { label: 'Flows', path: '/flows', icon: GitBranch },
    { label: 'Executions', path: '/executions', icon: History },
    { label: 'DLQ', path: '/dlq', icon: AlertTriangle },
    { label: 'Inspector', path: '/inspector', icon: Search },
  ];

  const adminItems: NavItem[] = [
    { label: 'Tags', path: '/tags', icon: Tag },
    { label: 'Apps', path: '/apps', icon: AppWindow },
    { label: 'Tokens', path: '/tokens', icon: KeyRound },
    { label: 'Env', path: '/env', icon: Variable },
    ...(role === 'Admin'
      ? [
          { label: 'Users', path: '/admin/users', icon: Users },
          { label: 'API Keys', path: '/admin/api-keys', icon: Key },
          { label: 'Backup', path: '/admin/backup', icon: DatabaseBackup },
        ]
      : []),
  ];

  const isActive = (path: string) => pathname.startsWith(path);

  const withTooltip = (label: string, node: React.ReactElement) =>
    collapsed ? (
      <Tooltip>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    ) : (
      node
    );

  const itemClass = (active: boolean) =>
    cn(
      'flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
      collapsed && 'justify-center px-0',
      active
        ? 'bg-sidebar-accent text-sidebar-foreground font-medium shadow-sm ring-1 ring-sidebar-border'
        : 'text-sidebar-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
    );

  const NavLink = ({ item }: { item: NavItem }) => {
    const Icon = item.icon;
    return withTooltip(
      item.label,
      <Link to={item.path} className={itemClass(isActive(item.path))}>
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </Link>,
    );
  };

  const ActionButton = ({
    label,
    icon: Icon,
    onClick,
    ariaLabel,
  }: {
    label: string;
    icon: LucideIcon;
    onClick: () => void;
    ariaLabel?: string;
  }) =>
    withTooltip(
      label,
      <button onClick={onClick} aria-label={collapsed ? ariaLabel ?? label : undefined} className={itemClass(false)}>
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span>{label}</span>}
      </button>,
    );

  return (
    <div className="flex h-screen bg-background text-foreground">
      <aside
        className={cn(
          'flex flex-col shrink-0 bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-[width] duration-200',
          collapsed ? 'w-14' : 'w-56',
        )}
      >
        <Link
          to="/flows"
          className={cn('flex items-center gap-2 h-14 shrink-0', collapsed ? 'justify-center' : 'px-4')}
        >
          <img src="/logo.svg" alt="FlowForge" className="h-7 w-7" />
          {!collapsed && <span className="text-[15px] font-semibold tracking-tight">FlowForge</span>}
        </Link>

        <div className="px-2 pb-2">
          {withTooltip(
            'Jump to flow (⌘K)',
            <button
              onClick={() => setSwitcherOpen(true)}
              aria-label="Jump to flow"
              className={cn(
                'flex w-full items-center gap-2 rounded-md border border-sidebar-border bg-sidebar-accent px-2.5 py-1.5 text-sm text-sidebar-muted shadow-sm hover:text-sidebar-foreground',
                collapsed && 'justify-center px-0',
              )}
            >
              <SquareSlash className="h-4 w-4 shrink-0" />
              {!collapsed && (
                <>
                  <span>Jump to flow</span>
                  <kbd className="ml-auto rounded border border-sidebar-border px-1 text-[10px]">⌘K</kbd>
                </>
              )}
            </button>,
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
          {topItems.map(item => (
            <NavLink key={item.path} item={item} />
          ))}

          <div className="pt-5 pb-1">
            {collapsed ? (
              <div className="mx-auto h-px w-6 bg-sidebar-border" />
            ) : (
              <div className="px-2.5 text-xs font-medium text-sidebar-muted">Admin</div>
            )}
          </div>

          {adminItems.map(item => (
            <NavLink key={item.path} item={item} />
          ))}
        </nav>

        <div className="px-2 py-2 space-y-0.5 border-t border-sidebar-border">
          <ActionButton
            label={isDark ? 'Light mode' : 'Dark mode'}
            ariaLabel={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            icon={isDark ? Sun : Moon}
            onClick={toggleDark}
          />
          <ActionButton label="Logout" icon={LogOut} onClick={logout} />
          <ActionButton
            label={collapsed ? 'Expand' : 'Collapse'}
            ariaLabel={collapsed ? 'Expand sidebar' : undefined}
            icon={collapsed ? ChevronsRight : ChevronsLeft}
            onClick={() => setCollapsed(c => !c)}
          />
        </div>
      </aside>
      <main className={cn('flex-1 min-w-0', FULL_BLEED.test(pathname) ? 'overflow-hidden' : 'overflow-auto px-8 py-6')}>
        {children}
      </main>
      <FlowQuickSwitcher open={switcherOpen} onOpenChange={setSwitcherOpen} />
    </div>
  );
}
